import {
  fetchSportEntities,
  getSportEntitiesList,
  getSportEntityById,
  getSportEntityBySportradarId,
  isSameSportEntity,
} from '@utils/entities'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import useFeatureFlagsSkeleton from './useFeatureFlagsSkeleton'

type DropFirst<T extends any[]> = T extends [any, ...infer Rest] ? Rest : []

type DropFirstAndSecond<T extends any[]> = T extends [any, any, ...infer Rest]
  ? Rest
  : []

interface SportEntitiesUtils {
  isSameSportEntity: (
    ...args: DropFirst<Parameters<typeof isSameSportEntity>>
  ) => ReturnType<typeof isSameSportEntity>
  getSportEntityById: (
    ...args: DropFirstAndSecond<Parameters<typeof getSportEntityById>>
  ) => ReturnType<typeof getSportEntityById>
  getSportEntityBySportradarId: (
    ...args: DropFirstAndSecond<Parameters<typeof getSportEntityBySportradarId>>
  ) => ReturnType<typeof getSportEntityBySportradarId>
  getSportEntitiesList: (
    ...args: DropFirstAndSecond<Parameters<typeof getSportEntitiesList>>
  ) => ReturnType<typeof getSportEntitiesList>
}

export type UseSportEntities = () => SportEntitiesUtils

const useSportEntities: UseSportEntities = () => {
  const isFollowingEnabled = useFeatureFlagsSkeleton().isFollowMyTeamEnabled

  const formattedEntities = useQuery({
    queryKey: ['sport-entities'],
    queryFn: () => fetchSportEntities({ isServerRequest: false }),
  }).data

  return useMemo(
    () => ({
      isSameSportEntity: (
        ...args: DropFirst<Parameters<typeof isSameSportEntity>>
      ) => isSameSportEntity(formattedEntities, ...args),
      getSportEntityById: (
        ...args: DropFirstAndSecond<Parameters<typeof getSportEntityById>>
      ) => getSportEntityById(formattedEntities, isFollowingEnabled, ...args),
      getSportEntityBySportradarId: (
        ...args: DropFirstAndSecond<
          Parameters<typeof getSportEntityBySportradarId>
        >
      ) =>
        getSportEntityBySportradarId(
          formattedEntities,
          isFollowingEnabled,
          ...args
        ),
      getSportEntitiesList: (
        ...args: DropFirstAndSecond<Parameters<typeof getSportEntitiesList>>
      ) => getSportEntitiesList(formattedEntities, isFollowingEnabled, ...args),
    }),
    [formattedEntities, isFollowingEnabled]
  )
}

export default useSportEntities
